import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Skeleton,
  Pagination,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Button,
  Tabs,
  Grid,
  TextField,
  InputAdornment,
  Tab,
  useTheme
} from '@mui/material';
import { Visibility, Delete, WarningAmber, Search, Clear, PersonAdd } from '@mui/icons-material';
import { toast } from 'react-toastify';
import palette from 'src/theme/palette';
import Page from 'src/components/Page';
import { getContact, deleteContact, addEditleads } from 'src/ApiServices';
import ContactModal from 'src/sections/dashboard-sections/ContactModal';
import ContactTrash from './ContactTrash';
import { Settings } from '@mui/icons-material';
import { Reply } from '@mui/icons-material';
import ContactReplyModal from 'src/sections/dashboard-sections/ContactReplyModal';

function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`contact-tabpanel-${index}`}
      aria-labelledby={`contact-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

export default function ContactUs() {
  // State Management
  const theme = useTheme();
  const [activeTab, setActiveTab] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [contactsData, setContactsData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [selectedContact, setSelectedContact] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [moveToLeadsModalOpen, setMoveToLeadsModalOpen] = useState(false);
  const [moveToLeadsLoading, setMoveToLeadsLoading] = useState(false);
  const [settingsModalOpen, setSettingsModalOpen] = useState(false);
  const [pagination, setPagination] = useState({
    current_page: 1,
    per_page: 20,
    total: 0,
    last_page: 1,
    has_more_pages: false
  });

  // Data Transformation Functions
  const transformContactData = (apiData) => {
    return apiData.map((contact) => ({
      id: contact.id || '-',
      name: contact.name || '-',
      email: contact.email || '-',
      contact_no: contact.contact_no || '-',
      message: contact.message || '-',
      response: contact.response || null,
      created_at: contact.created_at || '-',
      status: contact.status || 'pending',
      property_details: contact.property_details || null
    }));
  };

  // API Calls
  const fetchContactsData = async (currentPage = 1, search = '') => {
    try {
      setLoading(true);
      const response = await getContact({
        page: currentPage,
        search: search
      });
      const transformedData = transformContactData(response.data || []);
      setContactsData(transformedData);
      setPagination(
        response.pagination || {
          current_page: 1,
          per_page: 20,
          total: response.data?.length || 0,
          last_page: 1,
          has_more_pages: false
        }
      );
    } catch (error) {
      console.error('Error fetching contact requests:', error);
      setContactsData([]);
      toast.error('Failed to load contact requests');
    } finally {
      setLoading(false);
    }
  };

  // Effects
  useEffect(() => {
    if (activeTab === 0) {
      fetchContactsData(page, searchQuery);
    }
  }, [activeTab, page]);

  // Event Handlers
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    setPage(1);
    setSearchQuery('');
  };

  const handlePageChange = (event, value) => {
    setPage(value);
  };

  // Search handlers
  const handleSearchSubmit = useCallback(async () => {
    setPage(1);
    if (activeTab === 0) {
      await fetchContactsData(1, searchQuery);
    }
  }, [searchQuery, activeTab]);

  const handleClearSearch = useCallback(async () => {
    setSearchQuery('');
    setPage(1);
    if (activeTab === 0) {
      await fetchContactsData(1, '');
    }
  }, [activeTab]);

  const handleViewClick = (contact) => {
    setSelectedContact(contact);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedContact(null);
  };

  // Contact Operations
  const handleDeleteClick = (contact) => {
    setSelectedContact(contact);
    setDeleteModalOpen(true);
  };
  // Handler for contact setting
  const handleSettingsClick = (contact) => {
    setSelectedContact(contact);
    setSettingsModalOpen(true);
  };
  const refreshContacts = async () => {
    await fetchContactsData(page, searchQuery);
  };

  const handleSettingsClose = () => {
    setSettingsModalOpen(false);
    setSelectedContact(null);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedContact) return;

    try {
      setDeleteLoading(true);
      await deleteContact(selectedContact.id);
      toast.success('Contact request moved to trash successfully!');
      fetchContactsData(page, searchQuery);
      setDeleteModalOpen(false);
      setSelectedContact(null);
    } catch (error) {
      console.error('Failed to delete contact request:', error);
      if (error.response?.status === 404) {
        toast.error('Contact request not found!');
      } else if (error.response?.status === 403) {
        toast.error('Permission denied!');
      } else if (error.response?.status === 401) {
        toast.error('Unauthorized! Please login again.');
      } else {
        toast.error(error.response?.data?.message || 'Failed to delete contact request!');
      }
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteModalOpen(false);
    setSelectedContact(null);
  };

  // Move to Leads Operations
  const handleMoveToLeadsClick = (contact) => {
    setSelectedContact(contact);
    setMoveToLeadsModalOpen(true);
  };

  const handleMoveToLeadsConfirm = async () => {
    if (!selectedContact) return;

    try {
      setMoveToLeadsLoading(true);

      const propertyId = selectedContact.property_details?.id || null;

      const leadsPayload = {
        contact: selectedContact.contact_no,
        email: selectedContact.email,
        lead_type: 3,
        name: selectedContact.name,
        note: selectedContact.message || '',
        property_id: propertyId || '',
        lead_type_id: 3
      };

      await addEditleads(leadsPayload);
      toast.success('Contact successfully moved to leads!');
      fetchContactsData(page, searchQuery);
      setMoveToLeadsModalOpen(false);
      setSelectedContact(null);
    } catch (error) {
      console.error('Failed to move contact to leads:', error);

      if (error.response && error.response.data?.message) {
        toast.error(error.response.data.message);
      }
    } finally {
      setMoveToLeadsLoading(false);
    }
  };

  const handleMoveToLeadsCancel = () => {
    setMoveToLeadsModalOpen(false);
    setSelectedContact(null);
  };

  // Utility Functions
  const truncateMessage = (message, maxLength = 20) => {
    if (!message) return '-';
    if (message.length <= maxLength) return message;
    return `${message.substring(0, maxLength)}...`;
  };

  const truncateText = (text, maxLength = 30) => {
    if (!text) return 'N/A';
    return text.length > maxLength ? `${text.substring(0, maxLength)}...` : text;
  };

  // Render property details component
  const renderPropertyDetails = (contact) => {
    if (!contact.property_details) {
      return (
        <Typography
          sx={{
            color: palette.text.secondary,
            // fontStyle: 'italic',
            fontSize: '13px'
          }}
        >
          No Listing Attached
        </Typography>
      );
    }

    const property = contact.property_details;

    return (
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
        {/* Property Image */}
        <Box
          sx={{
            width: 60,
            height: 60,
            borderRadius: 1,
            overflow: 'hidden',
            flexShrink: 0,
            backgroundColor: palette.grey[200]
          }}
        >
          <img
            src={property.main_image}
            alt={property.title}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover'
            }}
            onError={(e) => {
              e.target.style.display = 'none';
              e.target.nextSibling.style.display = 'flex';
            }}
          />
          <Box
            sx={{
              width: '100%',
              height: '100%',
              display: 'none',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: palette.grey[300]
            }}
          >
            <Typography variant="caption" color="textSecondary">
              No Image
            </Typography>
          </Box>
        </Box>

        {/* Property Info */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Tooltip title={property.title} arrow>
            <Typography
              sx={{
                fontFamily: 'poppinsMedium',
                fontSize: '14px',
                color: palette.text.primary,
                lineHeight: 1.2,
                mb: 0.5,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}
            >
              {truncateText(property.title, 25)}
            </Typography>
          </Tooltip>

          <Tooltip title={property.address} arrow>
            <Typography
              sx={{
                fontSize: '12px',
                color: palette.text.secondary,
                lineHeight: 1.2,
                mb: 0.5,
                mt: 0.5,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}
            >
              {truncateText(property.address, 30)}
            </Typography>
          </Tooltip>

          <Typography
            sx={{
              fontSize: '11px',
              color: palette.text.disabled,
              lineHeight: 1.2,
              mt: 1.45
            }}
          >
            {property.city || 'N/A'} | {property.state || 'N/A'}
          </Typography>
        </Box>
      </Box>
    );
  };

  // Table Configuration
  const contactTableHeaders = [
    'Property Details',
    'Name',
    'Email',
    'Contact No',
    'Message',
    'Status',
    'Action'
  ];

  // Render Functions
  const renderSkeletonRows = (headers) =>
    Array.from({ length: 5 }).map((_, index) => (
      <TableRow key={index}>
        {headers.map((_, cellIndex) => (
          <TableCell key={cellIndex} sx={{ px: 2 }}>
            <Skeleton variant="text" height={25} />
          </TableCell>
        ))}
      </TableRow>
    ));

  const renderNoRecordsFound = (headers, message) => (
    <TableRow>
      <TableCell colSpan={headers.length} sx={{ textAlign: 'center', py: 8, borderBottom: 'none' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <Typography
            variant="h6"
            sx={{ color: palette.text.secondary, fontFamily: 'poppinsMedium', mb: 1 }}
          >
            {message.title}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: palette.grey[600],
              fontFamily: 'poppinsRegular',
              maxWidth: 400,
              textAlign: 'center'
            }}
          >
            {message.description}
          </Typography>
        </Box>
      </TableCell>
    </TableRow>
  );

  // Contact Table Component
  const ContactTable = () => (
    <Card>
      <TableContainer>
        <Table
          sx={{
            '& .MuiTableCell-head': {
              backgroundColor: palette.background.paper,
              color: palette.primary.tableHeadTitle,
              fontFamily: 'poppinsSemiBold',
              fontSize: '14px',
              borderBottom: `2px solid ${palette.divider}`,
              py: 2
            },
            '& .MuiTableCell-body': {
              color: palette.text.primary,
              fontFamily: 'poppinsRegular',
              fontSize: '14px',
              borderBottom: `1px solid ${palette.divider}`,
              py: 2
            }
          }}
        >
          <TableHead>
            <TableRow>
              {contactTableHeaders.map((header, index) => (
                <TableCell key={index} sx={{ px: 2, whiteSpace: 'nowrap' }}>
                  {header}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading
              ? renderSkeletonRows(contactTableHeaders)
              : contactsData.length > 0
              ? contactsData.map((contact) => (
                  <TableRow
                    key={contact.id}
                    sx={{
                      '&:hover': {
                        backgroundColor: palette.action.hover
                      }
                    }}
                  >
                    <TableCell sx={{ px: 2, minWidth: 200 }}>
                      {renderPropertyDetails(contact)}
                    </TableCell>
                    {/* <TableCell sx={{ px: 2 }}>{contact.id}</TableCell> */}
                    <TableCell sx={{ px: 2 }}>{contact.name}</TableCell>
                    <TableCell sx={{ px: 2 }}>{contact.email}</TableCell>
                    <TableCell sx={{ px: 2 }}>{contact.contact_no}</TableCell>
                    <TableCell sx={{ px: 2 }}>
                      <Tooltip title={contact.message || '-'} arrow>
                        <Typography
                          component="span"
                          sx={{
                            display: 'inline-block',
                            maxWidth: 200,
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            cursor: 'pointer'
                          }}
                        >
                          {truncateMessage(contact.message)}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell sx={{ px: 2 }}>{contact.status || 'N/A'}</TableCell>
                    <TableCell sx={{ px: 2 }}>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Tooltip title="Reply">
                          <IconButton
                            size="small"
                            onClick={() => handleSettingsClick(contact)}
                            sx={{
                              color: palette.info.main,
                              '&:hover': {
                                backgroundColor: palette.info.lighter
                              }
                            }}
                          >
                            {/* <Settings />  */}
                            <Reply />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="View Details">
                          <IconButton
                            size="small"
                            onClick={() => handleViewClick(contact)}
                            sx={{
                              color: palette.primary.main,
                              '&:hover': {
                                backgroundColor: palette.primary.lighter
                              }
                            }}
                          >
                            <Visibility />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Move to Leads">
                          <IconButton
                            size="small"
                            onClick={() => handleMoveToLeadsClick(contact)}
                            sx={{
                              color: palette.success.main,
                              '&:hover': {
                                backgroundColor: palette.success.lighter
                              }
                            }}
                          >
                            <PersonAdd />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Move to Trash">
                          <IconButton
                            size="small"
                            onClick={() => handleDeleteClick(contact)}
                            sx={{
                              color: palette.error.main,
                              '&:hover': {
                                backgroundColor: palette.error.lighter
                              }
                            }}
                          >
                            <Delete />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              : renderNoRecordsFound(contactTableHeaders, {
                  title: 'No Contact Requests Found',
                  description: searchQuery
                    ? `No contact requests found for "${searchQuery}". Try a different search term.`
                    : 'No contact requests available at the moment.'
                })}
          </TableBody>
        </Table>
      </TableContainer>
    </Card>
  );

  return (
    <Page title={`Contact Us | ${process.env.REACT_APP_PROJECTNAME}`}>
      <Box sx={{ p: { xs: 2, md: 3 } }}>
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 3,
            mt: 2,
            pb: 2,
            flexWrap: 'wrap',
            gap: 2
          }}
        >
          <Typography
            sx={{
              fontSize: { xs: 20, md: 25 },
              fontFamily: 'poppinsSemiBold',
              color: palette.primary.titledark
            }}
          >
            Contact Us
          </Typography>
        </Box>

        {/* Search Section */}
        <Grid container spacing={2} alignItems="center" sx={{ mb: 3 }}>
          <Grid item xs={12} md={10}>
            <TextField
              variant="outlined"
              placeholder="Search contacts..."
              size="medium"
              fullWidth
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearchSubmit()}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleSearchSubmit();
                }
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search sx={{ color: palette.text.secondary }} />
                  </InputAdornment>
                ),
                endAdornment: searchQuery && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={handleClearSearch} edge="end">
                      <Clear fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                )
              }}
              sx={{
                backgroundColor: theme.palette.background.paper,
                '& .MuiOutlinedInput-root': {
                  '& fieldset': { borderColor: theme.palette.divider },
                  '&:hover fieldset': { borderColor: theme.palette.primary.main },
                  '&.Mui-focused fieldset': { borderColor: theme.palette.primary.main }
                }
              }}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="contained"
                onClick={handleSearchSubmit}
                fullWidth
                sx={{
                  height: 56,
                  fontSize: '14px',
                  fontFamily: 'poppinsMedium'
                }}
                startIcon={<Search />}
              >
                Search
              </Button>
            </Box>
          </Grid>
        </Grid>

        {/* Tabs */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={activeTab} onChange={handleTabChange}>
            <Tab label="Contact Requests" id="contact-tab-0" sx={{ fontFamily: 'poppinsMedium' }} />
            <Tab label="Trash" id="contact-tab-1" sx={{ fontFamily: 'poppinsMedium' }} />
          </Tabs>
        </Box>

        {/* Contact Tab Panel */}
        <TabPanel value={activeTab} index={0}>
          <ContactTable />

          {/* Pagination for Contacts */}
          {!loading && contactsData.length > 0 && (
            <Box sx={{ display: 'flex', justifyContent: 'end', mt: 3 }}>
              <Pagination
                count={pagination.last_page}
                page={pagination.current_page}
                onChange={handlePageChange}
                color="primary"
              />
            </Box>
          )}
        </TabPanel>

        {/* Trash Tab Panel */}
        <TabPanel value={activeTab} index={1}>
          <ContactTrash searchQuery={searchQuery} onSearchChange={setSearchQuery} />
        </TabPanel>

        {/* Delete Confirmation Modal */}
        <Dialog
          open={deleteModalOpen}
          onClose={handleDeleteCancel}
          aria-labelledby="delete-dialog-title"
          aria-describedby="delete-dialog-description"
          PaperProps={{
            sx: {
              borderRadius: 2,
              padding: 1
            }
          }}
        >
          <DialogTitle id="delete-dialog-title" sx={{ textAlign: 'center', py: 3 }}>
            <WarningAmber
              sx={{
                fontSize: 60,
                color: palette.warning.main,
                mb: 2
              }}
            />
            <Typography
              variant="h6"
              sx={{ fontFamily: 'poppinsSemiBold', color: palette.text.primary }}
            >
              Confirm Deletion
            </Typography>
          </DialogTitle>
          <DialogContent sx={{ textAlign: 'center', pb: 2 }}>
            <DialogContentText
              id="delete-dialog-description"
              sx={{ color: palette.text.secondary, fontFamily: 'poppinsRegular' }}
            >
              Are you sure you want to move contact request from{' '}
              <strong>{selectedContact?.name}</strong> to trash?
            </DialogContentText>
          </DialogContent>
          <DialogActions
            sx={{
              justifyContent: 'center',
              gap: 2,
              pb: 3,
              px: 3,
              flexDirection: { xs: 'column', sm: 'row' }
            }}
          >
            <Button
              onClick={handleDeleteCancel}
              variant="outlined"
              sx={{
                borderColor: palette.grey[400],
                color: palette.text.primary,
                '&:hover': {
                  borderColor: palette.grey[600],
                  backgroundColor: palette.action.hover
                },
                minWidth: 100
              }}
              disabled={deleteLoading}
            >
              Cancel
            </Button>
            <Button
              onClick={handleDeleteConfirm}
              variant="contained"
              sx={{
                backgroundColor: palette.error.main,
                color: palette.error.contrastText,
                '&:hover': {
                  backgroundColor: palette.error.dark
                },
                minWidth: 100
              }}
              disabled={deleteLoading}
              startIcon={<Delete />}
            >
              {deleteLoading ? 'Moving...' : 'Move to Trash'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Move to Leads Confirmation Modal */}
        <Dialog
          open={moveToLeadsModalOpen}
          onClose={handleMoveToLeadsCancel}
          aria-labelledby="move-to-leads-dialog-title"
          aria-describedby="move-to-leads-dialog-description"
          PaperProps={{
            sx: {
              borderRadius: 2,
              padding: 1
            }
          }}
        >
          <DialogTitle id="move-to-leads-dialog-title" sx={{ textAlign: 'center', py: 3 }}>
            <PersonAdd
              sx={{
                fontSize: 60,
                color: palette.success.main,
                mb: 2
              }}
            />
            <Typography
              variant="h6"
              sx={{ fontFamily: 'poppinsSemiBold', color: palette.text.primary }}
            >
              Move to Leads
            </Typography>
          </DialogTitle>
          <DialogContent sx={{ textAlign: 'center', pb: 2 }}>
            <DialogContentText
              id="move-to-leads-dialog-description"
              sx={{ color: palette.text.secondary, fontFamily: 'poppinsRegular' }}
            >
              Are you sure you want to move contact request from{' '}
              <strong>{selectedContact?.name}</strong> to leads?
            </DialogContentText>
          </DialogContent>
          <DialogActions
            sx={{
              justifyContent: 'center',
              gap: 2,
              pb: 3,
              px: 3,
              flexDirection: { xs: 'column', sm: 'row' }
            }}
          >
            <Button
              onClick={handleMoveToLeadsCancel}
              variant="outlined"
              sx={{
                borderColor: palette.grey[400],
                color: palette.text.primary,
                '&:hover': {
                  borderColor: palette.grey[600],
                  backgroundColor: palette.action.hover
                },
                minWidth: 100
              }}
              disabled={moveToLeadsLoading}
            >
              Cancel
            </Button>
            <Button
              onClick={handleMoveToLeadsConfirm}
              variant="contained"
              sx={{
                backgroundColor: palette.success.main,
                color: palette.success.contrastText,
                '&:hover': {
                  backgroundColor: palette.success.dark
                },
                minWidth: 100
              }}
              disabled={moveToLeadsLoading}
              startIcon={<PersonAdd />}
            >
              {moveToLeadsLoading ? 'Moving...' : 'Move to Leads'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Contact Details Modal */}
        <ContactModal open={modalOpen} onClose={handleCloseModal} contact={selectedContact} />
        {/* Contact Settings Modal */}
        <ContactReplyModal
          open={settingsModalOpen}
          onClose={handleSettingsClose}
          contact={selectedContact}
          onStatusChange={refreshContacts}
        />
      </Box>
    </Page>
  );
}
